import React, { useState } from 'react';
import { Card, CardContent, CardActions, Typography, Button, Chip, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Snackbar } from '@mui/material';
import { 
  // GitHub as GitHubIcon, 
  Person as PersonIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { updateProject, deleteProject } from '../services/api';

const ProjectCard = ({ project, showStudentDetails = false, showEvaluate = false, onProjectUpdate, onProjectDelete }) => {
  const navigate = useNavigate();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: project.title || '',
    description: project.description || '',
    githubRepoUrl: project.githubUrl || project.githubRepoUrl || project.repoUrl || ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  // const githubLink = project.githubUrl || project.githubRepoUrl || project.repoUrl;

  const handleEditOpen = () => {
    setEditFormData({
      title: project.title || '',
      description: project.description || '',
      githubRepoUrl: project.githubUrl || project.githubRepoUrl || project.repoUrl || ''
    });
    setEditError('');
    setOpenEditDialog(true);
  };

  const handleEditClose = () => {
    setOpenEditDialog(false);
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async () => {
    try {
      if (!editFormData.title.trim() || !editFormData.githubRepoUrl.trim()) {
        setEditError('Title and GitHub URL are required');
        return;
      }
      
      setEditLoading(true);
      const response = await updateProject(project._id, {
        title: editFormData.title,
        description: editFormData.description,
        githubRepoUrl: editFormData.githubRepoUrl
      });
      
      if (response.data.project) {
        setUpdateSuccess(true);
        setOpenEditDialog(false);
        setTimeout(() => {
          if (onProjectUpdate) {
            onProjectUpdate(response.data.project);
          }
        }, 1500);
      }
    } catch (error) {
      setEditError(error.response?.data?.message || 'Failed to update project');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      await deleteProject(project._id);
      setDeleteSuccess(true);
      setOpenDeleteDialog(false);
      setTimeout(() => {
        if (onProjectDelete) {
          onProjectDelete(project._id);
        }
      }, 1500);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete project');
      setOpenDeleteDialog(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };


  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, flexWrap: 'wrap'  }}>
        <Typography gutterBottom variant="h5" component="h2">
          {project.title}
        </Typography>

        {showStudentDetails && project.student && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {project.student.name || 'Unknown Student'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({project.student.id || 'No Id'})
                </Typography>
            </Box>
            <Box>
              {project.student.branch && (
                <Chip label={project.student.branch} size="small" sx={{ mr: 0.5 }} />
              )}
              {project.student.yearOfStudy && (
                <Chip label={`Year ${project.student.yearOfStudy}`} size="small" variant="outlined" />
              )}
            </Box>
          </Box>
        )}

        {!showStudentDetails && <Typography variant="body2" color="text.secondary" paragraph>
          {project.description}
        </Typography>}
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>

        {/* {githubLink && (
          <Button 
            size="small" 
            startIcon={<GitHubIcon />} 
            href={githubLink} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            GitHub
          </Button>
        )} */}
        <Button 
          size="small" 
          variant='contained'
          color="secondary"
          onClick={() => navigate(`/project/${project._id}`)}
        >
          View Details
        </Button>
        {showEvaluate && (
          <Button 
            size="small" 
            color="tertiary"
            onClick={() => navigate(`/project/${project._id}/evaluate`)}
          >
            Evaluate
          </Button>
        )}
        <Button 
          size="small" 
          startIcon={<EditIcon />} 
          color="primary"
          onClick={handleEditOpen}
        >
          Edit
        </Button>
        <Button 
          size="small" 
          startIcon={<DeleteIcon />} 
          color="error"
          onClick={handleDeleteClick}
          disabled={deleteLoading}
        >
          Delete
        </Button>
      </CardActions>

      <Dialog open={openEditDialog} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField
            fullWidth
            label="Project Title"
            name="title"
            value={editFormData.title}
            onChange={handleEditChange}
            margin="normal"
            disabled={editLoading}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={editFormData.description}
            onChange={handleEditChange}
            margin="normal"
            multiline
            rows={3}
            disabled={editLoading}
          />
          <TextField
            fullWidth
            label="GitHub Repository URL"
            name="githubRepoUrl"
            value={editFormData.githubRepoUrl}
            onChange={handleEditChange}
            margin="normal"
            disabled={editLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading}>Cancel</Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
            disabled={editLoading}
          >
            {editLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this project? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={updateSuccess}
        autoHideDuration={4000}
        onClose={() => setUpdateSuccess(false)}
        message="Project updated successfully"
      />

      <Snackbar
        open={deleteSuccess}
        autoHideDuration={4000}
        onClose={() => setDeleteSuccess(false)}
        message="Project deleted successfully"
      />
    </Card>
  );
};

export default ProjectCard;
